import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { dashboardService } from 'services/dashboard.service';
import { CompanyDashboard } from 'types/dashboard';

interface LicenseStatus {
  isExpired: boolean;
  isLoading: boolean;
  error: string | null;
  remainingDays: number;
  hasLicense: boolean;
  companyData: CompanyDashboard | null;
}

export const useLicenseStatus = (): LicenseStatus => {
  const { user } = useAuth();
  const [isExpired, setIsExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [remainingDays, setRemainingDays] = useState(0);
  const [hasLicense, setHasLicense] = useState(false);
  const [companyData, setCompanyData] = useState<CompanyDashboard | null>(null);

  // Check if user is a company user (not admin)
  const isCompanyUser = Boolean(user && user.roleId && Number(user.roleId) !== 1);

  useEffect(() => {
    if (!isCompanyUser) {
      setIsLoading(false);
      setIsExpired(false);
      return;
    }

    const checkLicenseStatus = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const data = await dashboardService.getCompanyDashboard();
        setCompanyData(data);
        
        const licenseExists = data?.metrics?.hasLicense || false;
        const expired = data?.metrics?.isLicenseExpired || false;
        const days = data?.metrics?.remainingDaysToExpire || 0;
        
        setHasLicense(licenseExists);
        setIsExpired(expired);
        setRemainingDays(days);
      } catch (err: any) {
        setError(err?.message || 'Failed to check license status');
        setHasLicense(false);
        setIsExpired(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkLicenseStatus();
  }, [isCompanyUser, user?.id]);

  return {
    isExpired,
    isLoading,
    error,
    remainingDays,
    hasLicense,
    companyData,
  };
};
